import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { type EntitySelection } from "@salestrak/ui";
import { assigneeRefToEntityItem } from "#app/components/entity-selectors/utils";
import { USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import {
  TEAM_POOL_ID,
  teamToPoolItem,
} from "#app/components/entity-selectors/hooks/use-team-pool";
import { getInitialCustomFieldValue } from "#app/lib/custom-field-utils";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useCreateIssueData } from "#app/hooks/issue-view/use-create-issue-data";
import { useCreateIssueMutation } from "#app/hooks/issue-view/use-create-issue-mutation";
import { useScrollToElement } from "#app/lib/hooks/use-scroll-to-element";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";
import type { IssueRow } from "#app/lib/data";
import type {
  IssueTypeWithFields,
  IssueTypeFieldDef,
  IssueViewStage,
  CustomFieldDef,
} from "#app/components/issue-view/issue-view-types";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Draft state
// ---------------------------------------------------------------------------

export interface CreateIssueDraft {
  name: string;
  description: string;
  issueTypeId: string | null;
  workflowId: string;
  stageId: string | null;
  issueSourceId: string | null;
  address: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }> | null;
  assignees: EntitySelection[];
  teams: EntitySelection[];
  values: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

export interface CreateIssueContextValue {
  draft: CreateIssueDraft;
  updateDraft: (partial: Partial<CreateIssueDraft>) => void;
  updateCustomFieldValue: (cfId: string, value: unknown) => void;

  /** Custom fields for the currently selected issue type */
  issueTypeFields: IssueTypeFieldDef[];
  /** IDs of required custom fields for the selected issue type */
  requiredFieldIds: string[];
  /** Built-in property keys the selected type requires on create
   *  ("assignees", "teams", "address"). */
  requiredProperties: string[];
  /** Resolved custom field defs (for CustomFieldRenderer) keyed by customFieldId */
  customFieldDefs: CustomFieldDef[];
  /** Custom field defs map for quick lookup */
  customFieldDefsById: Record<string, CustomFieldDef>;

  /** Issue types with fields for the type selector */
  issueTypes: IssueTypeWithFields[];
  /** Stages for the current workflow */
  stages: IssueViewStage[];

  /** Whether data is still loading */
  loading: boolean;
  /** Whether the create mutation is in-flight */
  isSubmitting: boolean;
  /** Whether the form is valid and can be submitted */
  canSubmit: boolean;
  /** Number of required fields (including name) that are still empty */
  missingRequiredCount: number;
  /** Whether a submit has been attempted (triggers error display) */
  submitted: boolean;
  /** Submit the form (calls CreateIssue mutation) */
  submit: () => Promise<void>;
  /** Increments on each failed submit (triggers scroll + bar re-show). */
  submitAttemptCount: number;
  /**
   * True once it's safe to autofocus inputs. Desktop defaults to true;
   * the mobile sheet passes false until its open animation settles
   * (focusing mid-animation opens the virtual keyboard and desyncs
   * the sheet's translateY math — clips the handle, adds phantom padding).
   */
  canAutoFocus: boolean;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const CreateIssueContext = createContext<CreateIssueContextValue | null>(null);

/**
 * Returns the create-issue context value, or `null` when not in create mode.
 * Components use this to detect and branch for create mode.
 */
export function useCreateIssueMode() {
  return useContext(CreateIssueContext);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function isFieldValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/** The DOM ids of the built-in property rows a type can require — the
 *  `PropertyRow id` each carries, so a failed submit scrolls to the row. */
export const REQUIRED_PROPERTY_ROW_IDS: Record<string, string> = {
  assignees: "issue-assignees",
  teams: "issue-teams",
  address: "issue-address",
  source: "issue-source",
};

/** Whether the draft holds a value for a built-in property the type requires. */
function isPropertyEmpty(draft: CreateIssueDraft, key: string): boolean {
  switch (key) {
    case "assignees":
      return draft.assignees.length === 0;
    case "teams":
      return draft.teams.length === 0;
    case "address":
      return !draft.address?.wholeAddress?.trim();
    case "source":
      return isFieldValueEmpty(draft.issueSourceId);
    default:
      return false;
  }
}

/** Returns the DOM element ID of the first invalid field and the total missing count. */
function validateRequiredFields(
  draft: CreateIssueDraft,
  requiredFieldIds: string[],
  requiredProperties: string[],
): { firstInvalidId: string | null; missingCount: number } {
  const { name, values } = draft;
  let firstInvalidId: string | null = null;
  let missingCount = 0;

  if (!name.trim()) {
    firstInvalidId = "create-issue-name";
    missingCount++;
  }
  for (const key of requiredProperties) {
    if (isPropertyEmpty(draft, key)) {
      if (!firstInvalidId) firstInvalidId = REQUIRED_PROPERTY_ROW_IDS[key] ?? null;
      missingCount++;
    }
  }
  for (const fieldId of requiredFieldIds) {
    if (isFieldValueEmpty(values[fieldId])) {
      if (!firstInvalidId) firstInvalidId = `cf-${fieldId}`;
      missingCount++;
    }
  }
  return { firstInvalidId, missingCount };
}

/** A fresh values map for a type: every bound field at its type default. */
function fieldDefaultsFor(issueType: IssueTypeWithFields): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const f of issueType.fields ?? []) {
    if (f.field) values[f.customFieldId] = getInitialCustomFieldValue(f.field);
  }
  return values;
}

/**
 * The draft's values after a type change: the new type's field defaults, plus
 * every field the creator added by hand — a value whose key the OUTGOING type
 * did not carry. Those are the creator's own picks, not the type's, and a type
 * switch has no say over them.
 */
function retypeValues(
  values: Record<string, unknown>,
  fromType: IssueTypeWithFields | null,
  toType: IssueTypeWithFields | null,
): Record<string, unknown> {
  const fromIds = new Set((fromType?.fields ?? []).map((f) => f.customFieldId));
  const added: Record<string, unknown> = {};
  for (const [cfId, value] of Object.entries(values)) {
    if (!fromIds.has(cfId)) added[cfId] = value;
  }
  return { ...(toType ? fieldDefaultsFor(toType) : {}), ...added };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type CreateIssueProviderProps = Omittable<{
  initialWorkflowId?: string;
  initialStageId?: string;
  // The nested object relaxes too — `Omittable` is not recursive, and the
  // create screens hand this the dialog's own `ModalState.address`.
  initialAddress?: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }>;
  initialName?: string;
  /** Seeds the draft's source. The default-source effect never overwrites a
   *  non-null draft source, so a seed survives; the user can still change it. */
  initialIssueSourceId?: string;
  /** The team the create was opened from (a team page). Seeds the Assigned
   *  Team ahead of the creator's own team preference. */
  initialTeamId?: string;
  /** Where the create flow was launched from — included in issue create_finish analytics. */
  source?: string;
  onCreated: (issue: IssueRow) => void;
  /** When false, gates inputs from autofocusing (e.g. while a sheet is animating). Defaults to true. */
  canAutoFocus?: boolean;
  children: React.ReactNode;
}>;

export function CreateIssueProvider({
  initialWorkflowId,
  initialStageId,
  initialAddress,
  initialName,
  initialIssueSourceId,
  initialTeamId,
  source,
  onCreated,
  canAutoFocus = true,
  children,
}: CreateIssueProviderProps) {
  const { currentUser, workflows } = useAppData();
  const {
    teamIds: viewerTeamIds,
    teamMap,
    customFields: workspaceCustomFields,
  } = useWorkspaceData();
  const { settings: issueSourceSettings } = useIssueSourceSettings();

  // Resolve the effective workflowId:
  // - If provided, use it directly
  // - Otherwise, fall back to the default workflow (or first workflow)
  const resolvedWorkflowId = useMemo(() => {
    if (initialWorkflowId) return initialWorkflowId;
    const defaultWf = workflows.find((wf) => wf.isDefault);
    return defaultWf?.workflowId ?? workflows[0]?.workflowId ?? null;
  }, [initialWorkflowId, workflows]);

  const [draft, setDraft] = useState<CreateIssueDraft>({
    name: initialName ?? "",
    description: "",
    issueTypeId: null,
    workflowId: resolvedWorkflowId ?? "",
    stageId: initialStageId ?? null,
    issueSourceId: initialIssueSourceId ?? null,
    address: initialAddress ?? null,
    assignees: [],
    teams: [],
    values: {},
  });

  // Sync draft.workflowId when resolvedWorkflowId settles (e.g. workflows load after mount)
  useEffect(() => {
    if (resolvedWorkflowId && !draft.workflowId) {
      setDraft((prev) => ({ ...prev, workflowId: resolvedWorkflowId }));
    }
  }, [resolvedWorkflowId, draft.workflowId]);

  // Fetch issue types + stages for the draft's current workflow
  const {
    issueTypes,
    stages,
    defaultIssueTypeId,
    defaultStageId,
    loading,
  } = useCreateIssueData(draft.workflowId);

  // Latches for the two PEOPLE defaults (assignee, team) — see their effects
  // below. Type and Stage carry no latch: they are required, so an empty slot
  // is always "not defaulted yet", never a deliberate clear.
  const assigneeDefaultAppliedRef = useRef(false);
  const teamDefaultAppliedRef = useRef(false);

  // The per-user "Auto-assign to self" preference gates the default below.
  // null = never set, which is ON — the shipped default.
  const { preferences } = useUserSettings();
  const autoAssignSelf = preferences?.autoAssignSelf ?? true;
  const preferencesLoaded = preferences != null;
  const autoAssignTeam = preferences?.autoAssignTeam ?? true;
  const preferredTeamIds = preferences?.defaultTeamIds;

  // Build default assignee selection from the current user. The item shape comes
  // from the shared assignee-ref mapper so label/avatar resolution (suspended
  // muting, name→email fallback) can't fork from the entity system.
  const defaultAssigneeSelection = useMemo<EntitySelection | null>(() => {
    if (!currentUser || !autoAssignSelf) return null;
    return {
      id: currentUser.userId,
      poolId: USER_POOL_ID,
      entity: assigneeRefToEntityItem({
        __typename: "EntityRef" as const,
        entityType: "user",
        entityId: currentUser.userId,
        // currentUser is always an active member; the owner-ref user shape
        // requires a non-null memberStatus.
        user: { ...currentUser, memberStatus: "active" },
        team: null,
        isPrimary: true,
      }),
    };
  }, [currentUser, autoAssignSelf]);

  // The draft's starting Assigned Team, in order:
  // 1. The team the create was opened from (a team page) — the screen already
  //    answered the question, so it wins over any preference.
  // 2. "Auto-assign my team" off → nothing.
  // 3. The creator's default teams, kept to the teams they are still on.
  // 4. A creator on exactly ONE team gets it — there is no ambiguity to resolve.
  // Two or more teams with no default stay empty, and the picker's "Suggested"
  // MRU section carries the shortcut instead: assigning every team a creator
  // belongs to inflates every team's counts.
  const defaultTeamSelections = useMemo<EntitySelection[]>(() => {
    const toSelection = (teamId: string): EntitySelection[] => {
      const team = teamMap.get(teamId);
      return team ? [{ id: team.teamId, poolId: TEAM_POOL_ID, entity: teamToPoolItem(team) }] : [];
    };
    if (initialTeamId) return toSelection(initialTeamId);
    // Wait for the preferences: the latch below applies once, so a pass taken
    // on the unset-means-ON fallback would fill a team for someone who turned
    // the preference off.
    if (!preferencesLoaded || !autoAssignTeam) return [];
    const preferred = (preferredTeamIds ?? []).filter((id) => viewerTeamIds.includes(id));
    if (preferred.length > 0) return preferred.flatMap(toSelection);
    const [onlyTeamId] = viewerTeamIds;
    return viewerTeamIds.length === 1 && onlyTeamId ? toSelection(onlyTeamId) : [];
  }, [initialTeamId, preferencesLoaded, autoAssignTeam, preferredTeamIds, viewerTeamIds, teamMap]);

  // The draft's Type fills itself: the moment the workspace default is known
  // (the server's own fallback for an omitted id) it fills an empty slot,
  // with that type's field defaults. Reactive, not a one-shot latch — the types
  // arrive with the workflow-keyed query, and a drawer opened before the
  // workspace bootstrap lands (a fresh workspace, a cold load) has no workflow
  // to key it by yet, so a pass taken at mount would find nothing and never
  // run again. The selector cannot clear a type, so an empty slot only ever
  // means "not defaulted yet".
  useEffect(() => {
    if (loading || !defaultIssueTypeId) return;
    setDraft((prev) => {
      if (prev.issueTypeId) return prev;
      const issueType = issueTypes.find((it) => it.issueTypeId === defaultIssueTypeId);
      return {
        ...prev,
        issueTypeId: defaultIssueTypeId,
        values: issueType ? retypeValues(prev.values, null, issueType) : prev.values,
      };
    });
  }, [loading, defaultIssueTypeId, issueTypes]);

  // Stage follows the same rule for the draft's CURRENT workflow: a workflow
  // switch clears it, and the switched-to workflow's default stage refills it
  // once that workflow's stages load. The workflow guard is what keeps a stale
  // stage list — the previous workflow's, still in hand while the new one
  // loads — from filling the slot with a stage the workflow does not own.
  useEffect(() => {
    if (loading || !defaultStageId) return;
    const defaultStage = stages.find((s) => s.stageId === defaultStageId);
    if (!defaultStage) return;
    setDraft((prev) => {
      if (prev.stageId || defaultStage.workflowId !== prev.workflowId) return prev;
      return { ...prev, stageId: defaultStageId };
    });
  }, [loading, defaultStageId, stages]);

  // Default primary assignee to the current user, ONCE. The latch closes only
  // when the default has actually been applied — a pass taken before the
  // current user resolves must not spend it.
  useEffect(() => {
    if (assigneeDefaultAppliedRef.current || !defaultAssigneeSelection) return;
    assigneeDefaultAppliedRef.current = true;
    setDraft((prev) =>
      prev.assignees.length === 0
        ? { ...prev, assignees: [defaultAssigneeSelection] }
        : prev,
    );
  }, [defaultAssigneeSelection]);

  // Teams ride their own effect rather than the defaults pass above: the
  // workspace team list resolves independently of the workflow's types and
  // stages, so folding it in would drop the default whenever teams land second.
  // The ref closes the other end — once the creator has touched the row, a late
  // arrival must not refill a row they deliberately cleared.
  useEffect(() => {
    if (teamDefaultAppliedRef.current || defaultTeamSelections.length === 0) return;
    teamDefaultAppliedRef.current = true;
    setDraft((prev) =>
      prev.teams.length === 0 ? { ...prev, teams: defaultTeamSelections } : prev,
    );
  }, [defaultTeamSelections]);

  // Pre-populate the source with the PROJECT default source, mirroring the
  // fallback create() applies server-side when the input omits one — the
  // form shows what will be saved. Reactive, not a one-shot latch: the
  // settings resolve independently of the issue-type/stage data
  // (cache-and-network), so a default arriving only with the network
  // response — or absent from a stale cached page — still lands. A source
  // already on the draft is never overwritten. The server already reads the
  // default as null when its source is archived, so the id is used as-is.
  const defaultIssueSourceId = issueSourceSettings.projectDefaultSourceId;
  // With the project source policy "off" the row never renders, so nothing
  // is seeded either — mirrors create() skipping its default fallback.
  const projectSourceOff = issueSourceSettings.projectSourcePolicy === "off";
  useEffect(() => {
    if (!defaultIssueSourceId || projectSourceOff) return;
    setDraft((prev) =>
      prev.issueSourceId ? prev : { ...prev, issueSourceId: defaultIssueSourceId },
    );
  }, [defaultIssueSourceId, projectSourceOff]);

  // Computed: fields for the selected issue type
  const selectedIssueType = useMemo(
    () => issueTypes.find((it) => it.issueTypeId === draft.issueTypeId) ?? null,
    [issueTypes, draft.issueTypeId],
  );

  const issueTypeFields = useMemo(
    () => selectedIssueType?.fields ?? [],
    [selectedIssueType],
  );

  // Derived, not fetched: `requiredFieldIds` is not a column on IssueType,
  // it is the required bindings' ids.
  const requiredFieldIds = useMemo(
    () =>
      (selectedIssueType?.fields ?? [])
        .filter((f) => f.required)
        .map((f) => f.customFieldId),
    [selectedIssueType],
  );
  // The type's required built-ins, less Source while the workspace has
  // sources OFF for projects: the row is not drawn, so nothing can demand it
  // (the server stands the requirement down the same way).
  const requiredProperties = useMemo(
    () =>
      (selectedIssueType?.requiredProperties ?? []).filter(
        (key) => key !== "source" || !projectSourceOff,
      ),
    [selectedIssueType, projectSourceOff],
  );

  // The whole workspace pool, not just the type's bindings: the draft's Fields
  // section adds workspace fields the same way a settled Project does, and the
  // added field's def must resolve for its row and for the create input's value
  // normalization. The type's own defs are unioned in for a field the pool
  // query has not returned yet.
  const customFieldDefs = useMemo(() => {
    const seen = new Set(workspaceCustomFields.map((f) => f.customFieldId));
    const typeOnly = issueTypeFields
      .filter((f): f is IssueTypeFieldDef & { field: CustomFieldDef } => f.field !== null)
      .map((f) => f.field)
      .filter((f) => !seen.has(f.customFieldId));
    return [...workspaceCustomFields, ...typeOnly];
  }, [issueTypeFields, workspaceCustomFields]);

  const customFieldDefsById = useMemo(() => {
    const map: Record<string, CustomFieldDef> = {};
    for (const f of customFieldDefs) {
      map[f.customFieldId] = f;
    }
    return map;
  }, [customFieldDefs]);

  // Update draft
  const updateDraft = useCallback((partial: Partial<CreateIssueDraft>) => {
    // Any deliberate edit to the row closes the single-team default's window,
    // including clearing it back to empty.
    if (partial.teams !== undefined) teamDefaultAppliedRef.current = true;
    setDraft((prev) => {
      const next = { ...prev, ...partial };

      // When issue type changes, reset the type's custom field values to the
      // new type's defaults; fields the creator added by hand survive.
      if (partial.issueTypeId !== undefined && partial.issueTypeId !== prev.issueTypeId) {
        next.values = retypeValues(
          prev.values,
          issueTypes.find((it) => it.issueTypeId === prev.issueTypeId) ?? null,
          issueTypes.find((it) => it.issueTypeId === partial.issueTypeId) ?? null,
        );
      }

      return next;
    });
  }, [issueTypes]);

  // `undefined` REMOVES the field from the draft (the row's Remove action): a
  // key left behind with no value would still read as a field on the draft.
  const updateCustomFieldValue = useCallback((cfId: string, value: unknown) => {
    setDraft((prev) => {
      const values = { ...prev.values };
      if (value === undefined) delete values[cfId];
      else values[cfId] = value;
      return { ...prev, values };
    });
  }, []);

  // Submission — enrich the created issue with the stage icon color before notifying parent
  const handleCreated = useCallback(
    (issue: IssueRow) => {
      const stage = stages.find((s) => s.stageId === issue.stageId);
      if (stage?.icon) {
        onCreated({
          ...issue,
          icon: {
            ...issue.icon,
            color: stage.icon.color ?? issue.icon?.color ?? null,
            icon: issue.icon?.icon ?? null,
          },
        });
      } else {
        onCreated(issue);
      }
    },
    [onCreated, stages],
  );
  const { createIssue, isSubmitting } = useCreateIssueMutation({ onSuccess: handleCreated, source });
  const [submitted, setSubmitted] = useState(false);
  const { attemptCount: submitAttemptCount, scrollTo } = useScrollToElement();

  const { canSubmit, missingRequiredCount } = useMemo(() => {
    const result = validateRequiredFields(draft, requiredFieldIds, requiredProperties);
    return { canSubmit: result.missingCount === 0, missingRequiredCount: result.missingCount };
  }, [draft, requiredFieldIds, requiredProperties]);

  const submit = useCallback(async () => {
    setSubmitted(true);
    const { firstInvalidId } = validateRequiredFields(draft, requiredFieldIds, requiredProperties);
    if (firstInvalidId) {
      scrollTo(firstInvalidId);
      return;
    }
    await createIssue(draft, customFieldDefsById);
  }, [
    createIssue,
    draft,
    customFieldDefsById,
    requiredFieldIds,
    requiredProperties,
    scrollTo,
  ]);

  // Context value
  const value = useMemo<CreateIssueContextValue>(
    () => ({
      draft,
      updateDraft,
      updateCustomFieldValue,
      issueTypeFields,
      requiredFieldIds,
      requiredProperties,
      customFieldDefs,
      customFieldDefsById,
      issueTypes,
      stages,
      loading,
      isSubmitting,
      canSubmit,
      missingRequiredCount,
      submitted,
      submit,
      submitAttemptCount,
      canAutoFocus,
    }),
    [
      draft,
      updateDraft,
      updateCustomFieldValue,
      issueTypeFields,
      requiredFieldIds,
      requiredProperties,
      customFieldDefs,
      customFieldDefsById,
      issueTypes,
      stages,
      loading,
      isSubmitting,
      canSubmit,
      missingRequiredCount,
      submitted,
      submit,
      submitAttemptCount,
      canAutoFocus,
    ],
  );

  return (
    <CreateIssueContext.Provider value={value}>
      {children}
    </CreateIssueContext.Provider>
  );
}
